/**
 * Canonical display label for a broker. The broker IS the person, not the
 * LLC — so the broker's own name disambiguates one broker from another and is
 * therefore primary; the company/LLC name is only a fallback when no name is
 * on file. Mirrors the LEAD convention (FIND-05/FIND-07) and the server-side
 * BrokerLabelHelper (FIND-20/FIND-21).
 */
export function getBrokerDisplayName(b: {
  name?: string | null;
  companyName?: string | null;
}): string {
  const person = (b.name ?? "").trim();
  if (person) return person;
  const company = (b.companyName ?? "").trim();
  if (company) return company;
  return "Broker";
}

export interface BrokerPickerLabel {
  /** Headline — company (DBA-qualified when present). */
  primary: string;
  /** Disambiguators — "Contact name · email" (either may be absent). */
  secondary: string;
}

/**
 * PR 2 (broker-search disambiguation) — the label for a broker in a PICKER,
 * where staff choose among many brokers and need to tell them apart. Company is
 * the headline and "Contact name · Email" the secondary line. This is
 * deliberately company-first (the picker groups by firm), distinct from
 * {@link getBrokerDisplayName} which is the name-first CRM label.
 */
export function brokerPickerLabel(b: {
  companyName?: string | null;
  dba?: string | null;
  name?: string | null;
  email?: string | null;
}): BrokerPickerLabel {
  const company = (b.companyName ?? "").trim();
  const dba = (b.dba ?? "").trim();
  const primary = dba ? `${company || "Broker"} (${dba})` : company || "Broker";
  const secondary = [(b.name ?? "").trim(), (b.email ?? "").trim()]
    .filter((s) => s.length > 0)
    .join(" · ");
  return { primary, secondary };
}
